import { useState, useRef, useEffect } from "react";
import { FileUploader } from "react-drag-drop-files";
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { useMutation } from "@tanstack/react-query";
import { postProfileImage } from "../../../js/api/my-style/post-profile-image";
import { useDebounceEffect } from "./lib/use-debounce-effect";
import { canvasPreview } from "./lib/canvas-preview";
import { SharedIcon } from "../shared/shared-icon";
import { sizeRuleMessage, tooLargeMessage } from "../../../js/upload-limits";

const ASPECT_CROP = 1;

/**
 * The rules stated at the point of upload. The size figure comes from the configured limit
 * (see upload-limits.js) — it used to be the literal "5Mb" while the collection image form
 * said 2Mb for the same feature (T212719).
 */
const UploaderInner = ({ maxFileSizeBytes }) => {
  return (
    <div className="profile-image__uploader-inner">
      <p>
        <span className="profile-image__uploader-inner--drag">
          Drag photos here or{" "}
        </span>
        <span className="profile-image__uploader-inner--browse">
          browse files
        </span>
      </p>

      <p>
        PNG, JPG or JPEG.
        <br />
        {sizeRuleMessage(maxFileSizeBytes)}
      </p>
    </div>
  );
};

export const ProfileImageModal = ({ closeModal, maxFileSizeBytes }) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [avatar, setAvatar] = useState(null);
  const [crop, setCrop] = useState();
  const [file, setFile] = useState({
    name: "",
    type: "",
  });
  const [uploadError, setUploadError] = useState({
    sizeError: null,
    typeError: false,
    moderationError: null,
  });
  const updateAvatar = useMutation({
    mutationFn: postProfileImage,
    onSuccess: () => {
      window.location.reload();
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "The image could not be uploaded. Please try a different image.";
      setUploadError((prev) => ({
        ...prev,
        moderationError: message,
      }));
    },
  });

  useDebounceEffect(
    async () => {
      if (crop?.width && crop?.height && imgRef.current && canvasRef.current) {
        canvasPreview(imgRef.current, canvasRef.current, crop);
      }
    },
    100,
    [crop]
  );

  function onSelectFile(file) {
    if (!file) {
      return;
    }

    setUploadError({ sizeError: null, typeError: false, moderationError: null });

    /*
     * Size is checked HERE rather than by FileUploader's own maxSize gate, because that gate
     * hands its callback nothing but a fixed "File size is too big" string — it cannot name the
     * file's actual size, so the message it produces is weaker than the server's. Checking the
     * File ourselves gives us the same three things the server's message gives: what the file
     * is, what the limit is, and what to do about it (T212719 criterion 2).
     */
    if (file.size > maxFileSizeBytes) {
      setUploadError({
        sizeError: tooLargeMessage(file.size, maxFileSizeBytes),
        typeError: false,
        moderationError: null,
      });
      setAvatar(null);
      setIsDisabled(true);
      return;
    }

    setIsDisabled(false);
    setFile({
      name: file.name,
      type: file.type,
    });
    const reader = new FileReader();
    reader.addEventListener("load", () =>
      setAvatar(reader.result?.toString() || "")
    );
    reader.readAsDataURL(file);
  }

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;

    setCrop(
      centerCrop(
        convertToPixelCrop(
          makeAspectCrop(
            {
              unit: "%",
              height: 80,
            },
            ASPECT_CROP,
            width,
            height
          ),
          width,
          height
        ),
        width,
        height
      )
    );
  }

  const uploadHandler = async () => {
    const image = imgRef.current;
    const canvas = canvasRef.current;

    // All three, not any one: each is dereferenced below, so a missing one was a TypeError.
    if (image && canvas && crop) {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const exportWidth = Math.round(crop.width * scaleX);
      const exportHeight = Math.round(crop.height * scaleY);

      const offscreen = new OffscreenCanvas(exportWidth, exportHeight);
      const context = offscreen.getContext("2d");

      context.fillStyle = "#fff";
      context.fillRect(0, 0, exportWidth, exportHeight);

      context.drawImage(
        canvas,
        0,
        0,
        canvas.width,
        canvas.height,
        0,
        0,
        offscreen.width,
        offscreen.height
      );

      const blob = await offscreen.convertToBlob({
        type: "image/jpeg",
        quality: 0.82,
      });

      const formData = new FormData();
      formData.append("ProfileImage", blob, file.name);

      updateAvatar.mutate(formData);
    }
  };

  if (updateAvatar.isPending || updateAvatar.isSuccess) {
    return (
      <div className="saved-colors-loading-container">
        <SharedIcon
          iconName="#sprite-spinner"
          className="animate-spin"
        />
      </div>
    );
  }

  return (
    <>
      <div className="profile-image__header">
        <h4 className="profile-image__title">Upload a photo</h4>
      </div>
      <div className="profile-image__main">
        {!avatar && (
          <FileUploader
            name="file"
            classes={"profile-image__uploader"}
            handleChange={onSelectFile}
            children={<UploaderInner maxFileSizeBytes={maxFileSizeBytes} />}
            types={["JPG", "PNG", "JPEG"]}
            onTypeError={() =>
              setUploadError({ sizeError: null, typeError: true, moderationError: null })
            }
          />
        )}
        {uploadError.sizeError && (
          <p className="profile-image__uploader-validation" data-testid="image-upload-error" role="alert">
            {uploadError.sizeError}
          </p>
        )}
        {uploadError.typeError && !uploadError.sizeError && (
          <p className="profile-image__uploader-validation" data-testid="image-upload-error" role="alert">
            Only PNG, JPG or JPEG allowed.
          </p>
        )}
        {uploadError.moderationError && (
          <p className="profile-image__uploader-validation" data-testid="image-upload-error" role="alert">
            {uploadError.moderationError}
          </p>
        )}

        {!!avatar && (
          <>
            <ReactCrop
              aspect={ASPECT_CROP}
              crop={crop}
              onChange={(c) => setCrop(c)}
              circularCrop
              keepSelection
            >
              <img src={avatar} ref={imgRef} onLoad={onImageLoad} />
            </ReactCrop>

            <canvas ref={canvasRef} style={{ display: "none" }} />
          </>
        )}
      </div>
      <div className="profile-image__footer">
        <div className="profile-image__buttons-wrapper">
          <button onClick={closeModal} className="my-profile__submit-button">
            Cancel
          </button>
          <button
            onClick={uploadHandler}
            className="my-profile__submit-button my-profile__submit-button--dark"
            disabled={isDisabled}
          >
            Upload
          </button>
        </div>
      </div>
    </>
  );
};
